import { useMemo } from 'react'
import { Dialog, DialogHeader, DialogBody, Tooltip } from '@material-tailwind/react'
import PropTypes from 'prop-types'

import { AccessResult, ResultObservations } from '@/shared/components/access-result'
import { GunIcon } from '@/pages/access-control/access-form/gun-icon'

const looksLikeRut = (s) => {
  if (!s) return false
  const cleaned = String(s).toUpperCase().replace(/[^0-9K]/g, '')
  return /^[0-9]{7,8}[0-9K]$/.test(cleaned)
}

const formatRut = (s) => {
  const cleaned = String(s).toUpperCase().replace(/[^0-9K]/g, '')
  if (cleaned.length < 2) return s
  const body = cleaned.slice(0, -1)
  const dv = cleaned.slice(-1)
  return `${body}-${dv}`
}

const buildContractLabel = (row) => (
  row?.person_snapshot?.contract_label
    || row?.personSnapshot?.contract_label
    || row?.vehicle_snapshot?.contract_label
    || row?.vehicleSnapshot?.contract_label
    || [
      row?.person_snapshot?.contract_id
        ?? row?.personSnapshot?.contract_id
        ?? row?.vehicle_snapshot?.contract_id
        ?? row?.vehicleSnapshot?.contract_id,
      row?.person_snapshot?.contract_name
        ?? row?.personSnapshot?.contract_name
        ?? row?.vehicle_snapshot?.contract_name
        ?? row?.vehicleSnapshot?.contract_name
    ].filter(Boolean).join(' - ')
)

const toAccessResultLike = (row) => {
  const isExit = row?.event_type === 'exit' || row?.eventType === 'exit'
  const rawQuery = row?.query_text ?? row?.queryText ?? ''
  const fallbackDoc = looksLikeRut(rawQuery) ? formatRut(rawQuery) : rawQuery

  return ({
    access: !!row?.authorized,
    displayDoc: row?.person_snapshot?.rut ||
      row?.vehicle_snapshot?.patent ||
      row?.visit_snapshot?.document_number ||
      fallbackDoc,
    message: isExit ? 'Salida registrada' : (row?.authorized ? 'Autorizado' : 'No Autorizado'),
    observations: row?.observations || null,
    data: {
      full_name: row?.person_snapshot?.full_name || row?.visit_snapshot?.full_name,
      employee_role_name: row?.person_snapshot?.employee_role_name,
      company_name: row?.company_name || row?.person_snapshot?.company_name || row?.visit_snapshot?.company_name,
      contract_label: buildContractLabel(row)
    },
    detail: {
      verifications: row?.validation_result?.verifications || [],
      pending_documents: row?.validation_result?.pending_documents || []
    },
    event_type: row?.event_type || row?.eventType
  })
}

const wasPistol = (row) => {
  const verifs = row?.validation_result?.verifications
  if (!Array.isArray(verifs)) return false
  return verifs.some(v => v && v.pistol === true)
}

export function AccessLogDetailsModal({ open, onClose, row }) {
  const result = useMemo(() => (row ? toAccessResultLike(row) : null), [row])
  const pistol = wasPistol(row)
  const isExit = row?.event_type === 'exit' || row?.eventType === 'exit'


  return (
    <Dialog open={!!open} handler={onClose} size="lg">
      <DialogHeader>Detalles del Registro</DialogHeader>
      <DialogBody divider className="max-h-[75vh] overflow-auto">
        {pistol && (
          <div className="mb-2 flex items-center gap-2 text-gray-800">
            <Tooltip content="Leído con pistola">
              <div className="inline-flex h-7 w-7 items-center justify-center rounded bg-gray-100">
                <GunIcon className="h-4 w-4" />
              </div>
            </Tooltip>
            <span className="text-sm">Leído con pistola</span>
          </div>
        )}

        {result && (
          <AccessResult result={result}>
            {isExit && (
              <div className="mt-4 pt-3 border-t text-xs text-gray-500">
                Por mandato del <strong>artículo 19 N° 7</strong> de la Constitución Política de la República de Chile
                (libertad personal y seguridad individual), no procede la retención de personas; por ello,
                toda solicitud de salida se registra como permitida, sin perjuicio de las observaciones o
                pendientes informados.
              </div>
            )}
          </AccessResult>
        )}
      </DialogBody>
    </Dialog>
  )
}

AccessLogDetailsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object
}

export function AccessLogObservationsModal({ open, onClose, observations }) {
  return (
    <Dialog open={!!open} handler={onClose} size="md">
      <DialogHeader>Observaciones</DialogHeader>
      <DialogBody divider className="max-h-[60vh] overflow-auto">
        <ResultObservations observations={observations} />
      </DialogBody>
    </Dialog>
  )
}

AccessLogObservationsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  observations: PropTypes.oneOfType([PropTypes.string, PropTypes.null])
}
